/**
 * @file This file contains the form API functions.
 * @module api/formApi
 */

import { ENV } from '../config/runtimeEnv';
import { getSessionUser } from "../utils/auth/sessionUser";
import { Patient } from "../types/healthcare/PatientTypes";
import { TeamMember } from "../types/admin/TeamTypes";
import type { UserUuid } from "../types/identity";
import { http } from "../lib/http/client";
const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

export interface FormElement {
	id: string | number;
	type: string;
	label?: string;
	question?: string;
	placeholder?: string;
	options?: string[];
	required?: boolean;
}

export interface FormSection {
	id: string | number;
	title: string;
	description?: string;
	fields: FormElement[];
	order: number;
}

export interface Form {
	id: number;
	practitioner_id: UserUuid;
	department: string;
	title: string;
	structure: FormElement[] | FormSection[];
	created_at: Date;
	updated_at: Date;
	use_sections?: boolean;
	form_type?: 'patient' | 'practitioner';
	is_global?: boolean;
	admin_template_id?: number;
}

export const defaultForm: Form = {
	id: 0,
	practitioner_id: "",
	department: "",
	title: "",
	structure: [],
	created_at: new Date(),
	updated_at: new Date(),
	use_sections: false,
	form_type: 'patient',
};

export interface AssignedForm {
	id: number;
	practitioner_id: UserUuid;
	patient_id: UserUuid;
	form_id: number;
	notes: string;
	status: string;
	created_at: Date;
	updated_at: Date;
	reviewed_at?: Date;
	assignee_type?: 'patient' | 'practitioner';
	assigned_to_practitioner_id?: UserUuid;
	is_mine?: boolean;
	/**
	 * The per-assignment capability that opens `/patient/form`. Optional because it is
	 * `$hidden` on healthcare's model and surfaced ONLY by the two responses deliberately
	 * shaped to carry it (`GET /practitioner/forms/assign/{id}`, `GET /patient/{id}/forms`)
	 * plus the `POST /practitioner/forms/assign` 201. It is absent from the Forms In Tray
	 * dashboard by design — do not "fix" that by widening the model.
	 */
	access_token?: string;
	form?: Form;
	response?: FormResponse;
	patient?: Patient;
	practitioner?: TeamMember;
	assigned_to_practitioner?: TeamMember;
}

export interface FormResponse {
	id: number;
	assigned_form_id: number;
	response: string;
	created_at: Date;
	updated_at: Date;
	structure?: FieldResponse[];

	// ─── Provenance (ClickUp 86d3t4ev9 / F1) ──────────────────────────────────
	// ⚠️ ALL OPTIONAL AND ALL NULLABLE. Every row written before F1 carries NULL in
	// each of these, and a NULL is an honest "unknown" — never read a null
	// `submitted_via` as "the patient filled it in". Render "Submitted" with no
	// method rather than guessing.
	/** Auth-service user id (the JWT `sub`) of whoever submitted. Not a name. */
	submitted_by_user_id?: UserUuid | null;
	submitted_by_role?: 'patient' | 'practitioner' | null;
	/**
	 * ⚠️ READ side only, and WIDER than anything this client may SEND — see
	 * `PractitionerSubmittedVia` below. `import` (ClickUp 86d418z6b / S7.1) means the answers
	 * were migrated in from another practice-management system, so nobody in this platform
	 * was at the keyboard and `submitted_by_user_id` is null. Neither healthcare submit route
	 * accepts it, deliberately.
	 */
	submitted_via?: 'patient_link' | 'practitioner_on_behalf' | 'handover_device' | 'import' | null;
	/** Auth-service user id of whoever last corrected the answers (F4). */
	last_edited_by_user_id?: UserUuid | null;
	last_edited_at?: string | Date | null;
	/** 0 (or absent) until the first F4 edit; equals the newest `revision_number`. */
	edit_count?: number | null;
}

/**
 * One superseded body of a response, snapshotted by `PUT /practitioner/forms/results/{id}`
 * before it overwrote the answers (F4, ClickUp 86d3t4ev9).
 *
 * ⚠️ `response` here is the DECODED answers — an object/array — NOT the JSON string that
 * `FormResponse.response` carries. The two endpoints genuinely differ: the controller
 * snapshots `decodeAnswers($previousJson)` into a model whose `array` cast then
 * round-trips it as real JSON, while `form_responses.response` is handed a raw JSON
 * string and round-trips back out as that same string. Anything reading both must
 * tolerate both shapes — see `resolveResponseMap` in ReviewFormModal.
 */
export interface FormResponseRevision {
	id: number;
	form_response_id: number;
	response: FieldResponse[] | Record<string, any> | string;
	revised_by_user_id: UserUuid | null;
	revision_number: number;
	created_at: string | Date;
}


export interface FieldResponse {
	id: string | number;
	type: string;
	question: string;
	response: any;
}

export interface FileResponse {
	id: string | number;
	name: string;
	type: string;
	loaction: string;
	size: number;
}

export interface SectionProgress {
	sectionId: string | number;
	responses: Record<string, any>;
	completedAt?: Date;
}

export interface FormInTrayFilters {
	page_number?: number;
	rows?: number;
	assignee_type?: 'patient' | 'practitioner';
	filters?: {
		status?: string[];
		form_id?: number[];
		patient_name?: string;
		practitioner_id?: UserUuid[];
		assigned_to_practitioner_id?: UserUuid[];
		created_at?: any;
		updated_at?: any;
	};
	sort_field?: string;
	sort_order?: 'ascend' | 'descend';
}

// ─── Form Templates ───────────────────────────────────────────────────────────

export const fetchForms = async (search?: string, form_type?: 'patient' | 'practitioner'): Promise<{
	data: Form[];
}> => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/forms`,
		{
			params: { search, form_type },
		}
	);
	return { data: response.data.data };
};

export const fetchFormsDashboard = async (start_date: Date, end_date: Date): Promise<{
	data: AssignedForm[];
}> => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/forms/dashboard`,
		{
			params: { start_date, end_date },
		}
	);
	return { data: response.data.data };
};

export const fetchInTrayForms = async (filters: FormInTrayFilters): Promise<{
	data: AssignedForm[];
	total: number;
}> => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/forms/intray/dashboard`,
		{
			params: filters,
		}
	);
	return { data: response.data.data, total: response.data.total };
};

export const updateAssignedFormStatus = async (
	assignedFormId: number,
	status: string
): Promise<AssignedForm> => {
	const response = await http.put(
		`${API_BASE_URL}/practitioner/forms/assigned/${assignedFormId}/status`,
		{ status }
	);
	return response.data.data;
};

export const updateAssignedForm = async (
	assignedFormId: number,
	data: Partial<AssignedForm>
): Promise<AssignedForm> => {
	const response = await http.put(
		`${API_BASE_URL}/practitioner/forms/assigned/${assignedFormId}`,
		data
	);
	return response.data.data;
};

export const fetchSingleFormById = async (id: number): Promise<{ data: Form }> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/forms/single/${id}`);
	return { data: response.data };
};

export const setNewForm = async (formData: Partial<Form>): Promise<{ data: Form; message: string }> => {
	const user = getSessionUser();
	const payload = {
		...formData,
		structure: formData.structure ? JSON.stringify(formData.structure) : undefined,
	};
	const response = await http.post(`${API_BASE_URL}/practitioner/forms`, {
		formData: payload,
		user_id: user.uuid,
	});
	return { data: response.data.data, message: response.data.message };
};

export const updateForm = async (formData: Partial<Form>): Promise<{ data: Form; message: string }> => {
	const user = getSessionUser();
	const payload = {
		...formData,
		structure: formData.structure ? JSON.stringify(formData.structure) : undefined,
	};
	const response = await http.put(`${API_BASE_URL}/practitioner/forms/${formData.id}`, {
		formData: payload,
		user_id: user.uuid,
	});
	return { data: response.data.data, message: response.data.message };
};

export const deleteForm = async (formId: number) => {
	const response = await http.delete(`${API_BASE_URL}/practitioner/forms/${formId}`);
	return response.data;
};

// ─── Patient-Type Assignment ──────────────────────────────────────────────────

export const assignForm = async (assignedForm: Partial<AssignedForm>, patient_uuid: string) => {
	const response = await http.post(`${API_BASE_URL}/practitioner/forms/assign`, {
		assignedForm,
		user_uuid: getSessionUser().uuid,
		patient_uuid,
	});
	return response.data;
};

export const getAssignedForms = async (patientId: UserUuid): Promise<AssignedForm[]> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/forms/assign/${patientId}`);
	return response.data;
};

// ─── Practitioner-Type Assignment ─────────────────────────────────────────────

export const getPractitionerAssignedForms = async (
	practitionerId?: UserUuid,
	patientId?: UserUuid
): Promise<AssignedForm[]> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/forms/practitioner-assigned`, {
		params: { practitioner_id: practitionerId, patient_id: patientId },
	});
	return response.data;
};

export const getPractitionerFormToRender = async (assignedFormId: number): Promise<{ form: any; assigned: AssignedForm }> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/forms/practitioner-render/${assignedFormId}`);
	return { form: response.data.form, assigned: response.data.assignment };
};

export const uploadPractitionerFormResults = async (assignedFormId: number, results: string) => {
	const response = await http.post(`${API_BASE_URL}/practitioner/forms/practitioner-results`, {
		assigned_form_id: assignedFormId,
		response: results,
	});
	return response.data;
};

export const savePractitionerFormProgress = async (assignedFormId: number, progress: string) => {
	const response = await http.post(`${API_BASE_URL}/practitioner/forms/practitioner-progress`, {
		assigned_form_id: assignedFormId,
		progress,
	});
	return response.data;
};

export const getPractitionerFormProgress = async (assignedFormId: number) => {
	const response = await http.get(`${API_BASE_URL}/practitioner/forms/practitioner-progress/${assignedFormId}`);
	return response.data;
};

// ─── Practitioner Viewing Results ─────────────────────────────────────────────

/**
 * How a practitioner-submitted response to a PATIENT-typed form was captured.
 *
 * healthcare validates this with `in:practitioner_on_behalf,handover_device`
 * (`FormsController::StoreFormResponse`) — anything else is a 422, so the strings must
 * match byte for byte. Omitting the key is fine: the server defaults to
 * `practitioner_on_behalf`. `patient_link` is NOT in this union — that value is written
 * server-side by the patient-facing route and can never be claimed by this one. Neither is
 * `import` (S7.1): that is written by a bulk importer through healthcare's service layer, and
 * a live session claiming it would be stating that a human did not type what a human typed.
 */
export type PractitionerSubmittedVia = 'practitioner_on_behalf' | 'handover_device';

export const uploadFormResults = async (
	assignedFormId: number,
	results: string,
	submittedVia?: PractitionerSubmittedVia,
) => {
	const response = await http.post(`${API_BASE_URL}/practitioner/forms/results/`, {
		assigned_form_id: assignedFormId,
		response: results,
		// Sent only when the caller states it. An explicit `submitted_via: null` passes the
		// server's `nullable` rule and would store NULL — which the F1 migration defines as
		// "written before this feature existed", i.e. a lie about a row that was not.
		...(submittedVia ? { submitted_via: submittedVia } : {}),
	});
	return response.data;
};

/**
 * Record that the practitioner handed their device to the patient (F5, for F10).
 *
 * `POST /practitioner/forms/assigned/{id}/handover-started`, **no request body**, on the
 * practitioner's own token.
 *
 * ⚠️ It writes the `form.handover_started` audit row and NOTHING else — no mode flag, no
 * lock, no token. That is deliberate: an abandoned hand-over must leave the assignment
 * untouched, and there is no "un-hand-over" call the frontend could be relied on to make.
 * The caller already holds the access token from `GET /practitioner/forms/assign/{id}`.
 *
 * 404 for a practitioner-typed or missing assignment; 401 without a practitioner token.
 */
export const startFormHandover = async (
	assignedFormId: number,
): Promise<{ assigned_form_id: number; handover_started: boolean }> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/forms/assigned/${assignedFormId}/handover-started`,
		{},
	);
	return response.data;
};

/**
 * Return a signed-off assignment to `Pending` so it can be submitted again (S3 of ClickUp
 * 86d3y4ear).
 *
 * `POST /practitioner/forms/assigned/{id}/reopen`, **no request body**, on the practitioner's
 * own token — same route family and same auth group as `link-issued` / `handover-started`.
 *
 * ⚠️ THIS IS THE ONLY WAY BACK TO `Pending` FROM A SIGN-OFF. `Completed -> Pending` and
 * `Reviewed -> Pending` used to be ordinary values on the assignment PUT, indistinguishable
 * from a notes edit; S2 now refuses them there. Every controller path that accepts a
 * submission early-returns "Form already completed" once the row is `Completed`, so without
 * this call a form filled in error could never be redone.
 *
 * The submitted answers are deliberately LEFT ALONE — `form_response_revisions` already owns
 * answer history, and deleting clinical data on a reopen would be a much larger decision than
 * this route carries. Audited as its own `form.reopened` event, ids and vocabulary only.
 *
 * 422 for any other current status (reopening a `Pending` form is a no-op the caller should
 * hear about; a `Cancelled` one is an ordinary transition the PUT already allows); 404 for a
 * missing assignment; 401 without a practitioner token.
 */
export const reopenAssignedForm = async (
	assignedFormId: number,
): Promise<{ assigned_form_id: number; status: string; previous_status: string }> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/forms/assigned/${assignedFormId}/reopen`,
		{},
	);
	return response.data;
};

/**
 * The `link-issued` response. `access_token_expires_at` is an ISO-8601 string or `null`
 * ("never expires"), read straight off the assignment AFTER the mint — so it reflects what
 * the server actually stored, not what was asked for.
 */
export interface IssuedFormLink {
	assigned_form_id: number;
	access_token: string;
	access_token_expires_at: string | null;
	rotated: boolean;
}

/**
 * Issue (or re-issue) this assignment's patient link and record that a link was handed out
 * (F5, for F11).
 *
 * `POST /practitioner/forms/assigned/{id}/link-issued`, on the practitioner's own token.
 * Emits exactly ONE `form.link_issued` audit row per call, carrying `{form_id, rotated,
 * expires}` — `expires` is a BOOLEAN and the token value is never audited.
 *
 * ⚠️ `rotate: true` INVALIDATES EVERY LINK ALREADY SENT for this assignment, the emailed one
 * included. It is the kill switch for a QR left on a shared screen, never a default — hence
 * the explicit `?? false` rather than relying on the server's.
 *
 * ⚠️ `expiresInMinutes` is applied even when a token already exists, so it OVERWRITES the
 * stored `access_token_expires_at` (`AssignedForm::mintAccessToken()`'s idempotent branch).
 * There is one token per assignment, so a caller asking for a short window is shortening
 * every copy of that link, including one already in a patient's inbox. Omit it and the stored
 * expiry is left exactly as it was — passing nothing means "I don't care", not "clear it".
 *
 * 404 for a practitioner-typed or missing assignment; 422 for an out-of-range expiry
 * (1..43200); 401 without a practitioner token.
 */
export const issueFormLink = async (
	assignedFormId: number,
	options?: { rotate?: boolean; expiresInMinutes?: number | null },
): Promise<IssuedFormLink> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/forms/assigned/${assignedFormId}/link-issued`,
		{
			rotate: options?.rotate ?? false,
			// Omitted entirely when not asked for. An explicit `expires_in_minutes: null` passes
			// the server's `nullable` rule and means "no expiry supplied" — same outcome, but the
			// key's absence is the honest statement of intent.
			...(options?.expiresInMinutes != null ? { expires_in_minutes: options.expiresInMinutes } : {}),
		},
	);
	return response.data;
};

export const getFormResults = async (assignedFormId: number) => {
	const response = await http.get(`${API_BASE_URL}/practitioner/forms/results/${assignedFormId}`);
	return response.data;
};

/**
 * Correct an ALREADY-SUBMITTED patient response (F4, ClickUp 86d3t4ev9).
 *
 * PUT, not POST: `uploadFormResults` above CREATES a first submission and owns
 * `status = 'Completed'`, the `form_progress` delete and the provenance columns. This one
 * only ever edits — no response row is a 404, never a create.
 *
 * ⚠️ `results` is a JSON **string**, matching `uploadFormResults` — healthcare validates
 * `response` as `required|json`, so a plain object is a 422.
 *
 * ⚠️ `assigned_forms.status` is deliberately NOT touched by this call. A `Reviewed` form
 * stays `Reviewed`. Nothing a caller's list renders changes as a result of this request,
 * which is why the caller does not need to refetch that list afterwards.
 */
export const updateFormResponse = async (assignedFormId: number, results: string): Promise<FormResponse> => {
	const response = await http.put(`${API_BASE_URL}/practitioner/forms/results/${assignedFormId}`, {
		response: results,
	});
	return response.data;
};

/**
 * The superseded answers for an assignment, newest first (F4, ClickUp 86d3t4ev9).
 *
 * ⚠️ A patient-typed assignment with no response yet answers `{"revisions": []}` with a
 * **200**, deliberately asymmetric with the PUT's 404 — reading a history that does not
 * exist yet is not an error. Callers must not treat an empty list as a failure.
 */
export const getFormResponseRevisions = async (assignedFormId: number): Promise<FormResponseRevision[]> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/forms/results/${assignedFormId}/revisions`);
	return response.data?.revisions ?? [];
};

export const getFormToRender = async (assignedFormId: number): Promise<{ form: any; assigned: AssignedForm }> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/forms/render/${assignedFormId}`);
	return { form: response.data.form, assigned: response.data.assignment };
};

export const saveFormProgress = async (assignedFormId: number, progress: string) => {
	const response = await http.post(`${API_BASE_URL}/practitioner/forms/progress/`, {
		assigned_form_id: assignedFormId,
		progress,
	});
	return response.data;
};

export const getFormProgress = async (assignedFormId: number) => {
	const response = await http.get(`${API_BASE_URL}/practitioner/forms/progress/${assignedFormId}`);
	return response.data;
};

// ─── Patient-Facing APIs (patient-type forms only) ────────────────────────────
//
// These five are the routes healthcare guards with the `form.token` middleware
// (`EnsureFormAccessToken`). Their caller may be a patient with NO ACCOUNT AT ALL, so the
// only credential is the per-assignment `access_token`.
//
// ⚠️ NO SESSION NEEDED: the gateway serves these as `capability` routes (contract §4.6).
//
// TRANSPORT — this mirrors what the middleware reads, and the split is deliberate:
//   • GETs put the token on the query string (`?t=`). A patient pastes those URLs into a
//     browser bar; a header is not expressible there.
//   • POSTs put it in the `X-Form-Token` header, which keeps a live capability out of server
//     access logs. The middleware reads the query with `query('t')`, never from the POST
//     body, so a body token is not a way in.
// The header wins server-side when both are present.

const formTokenParams = (token?: string) => (token ? { t: token } : undefined);
const formTokenHeaders = (token?: string) => (token ? { "X-Form-Token": token } : undefined);

export const saveFormProgressPatient = async (assignedFormId: number, progress: string, token?: string) => {
	const response = await http.post(`${API_BASE_URL}/patient/forms/progress/`, {
		assigned_form_id: assignedFormId,
		progress,
	}, {
		headers: formTokenHeaders(token),
	});
	return response.data;
};

export const getFormProgressPatient = async (assignedFormId: number, token?: string) => {
	const response = await http.get(`${API_BASE_URL}/patient/forms/progress/${assignedFormId}`, {
		params: formTokenParams(token),
	});
	return response.data;
};

export const getFormResultsPatient = async (assignedFormId: number, token?: string) => {
	const response = await http.get(`${API_BASE_URL}/patient/forms/results/${assignedFormId}`, {
		params: formTokenParams(token),
	});
	return response.data;
};

/**
 * ⚠️ `submittedVia` IS SENT BUT NOT YET HONOURED BY healthcare — read this before relying on it.
 *
 * `StorePatientFormResponse` (`FormsController`) validates only `assigned_form_id` and
 * `response`, and writes `submitted_via => 'patient_link'` as a **hard-coded literal**. The
 * `in:practitioner_on_behalf,handover_device` rule F3 added lives on the *practitioner*
 * endpoint (`StoreFormResponse`) only. So a hand-over submitted through this function
 * currently stores `patient_link`, not `handover_device`.
 *
 * The argument exists anyway because F10's hand-over device genuinely is a distinct capture
 * method and the frontend knows it at submit time. Making the row say so needs a matching
 * healthcare change (accept `nullable|string|in:handover_device` here and prefer it over the
 * literal) — until that lands, treat the value as advisory, not as the record.
 *
 * Following `uploadFormResults`' convention, the key is OMITTED ENTIRELY when not supplied:
 * an explicit `submitted_via: null` passes a `nullable` rule and stores NULL, which F1
 * defines as "written before this feature existed".
 */
export const uploadPatientFormResults = async (
	assignedFormId: number,
	results: string,
	token?: string,
	submittedVia?: Extract<PractitionerSubmittedVia, 'handover_device'>,
) => {
	const response = await http.post(`${API_BASE_URL}/patient/forms/results`, {
		assigned_form_id: assignedFormId,
		response: results,
		...(submittedVia ? { submitted_via: submittedVia } : {}),
	}, {
		headers: formTokenHeaders(token),
	});
	return response.data;
};

/**
 * Upload one attachment for a `type: 'file'` field on a patient form (ClickUp 86d3y4e9p).
 *
 * ⚠️ THIS GOES TO healthcare, NOT TO THE FILE SERVER — that is the whole fix. The page used
 * to call `uploadFiles` (`api/file/fileServiceApi`), which needs a signed-in session. A patient
 * who followed an emailed or QR link has none — they may have no account at all — so the upload 401'd at the
 * file service and a form containing a file field could not be completed via a link at all.
 * healthcare authenticates the form token, derives the patient from the assignment, and
 * proxies to the file service with its own service credential.
 *
 * Same transport rule as the other POSTs above: the token rides the `X-Form-Token` header,
 * never the body. `Content-Type` is left unset so the browser writes the multipart boundary.
 *
 * Answers the file service's own `{ files: [...] }` envelope (minus `storage_path`), so
 * `toFileResponses` maps it unchanged.
 */
export const uploadPatientFormAttachment = async (assignedFormId: number, file: File, token?: string) => {
	const formData = new FormData();
	formData.append("assigned_form_id", String(assignedFormId));
	formData.append("file", file);

	const response = await http.post(`${API_BASE_URL}/patient/forms/attachments`, formData, {
		headers: formTokenHeaders(token),
	});
	return response.data;
};

export const getPatientFormToRender = async (assignedFormId: number, token?: string): Promise<{ form: any; assigned: AssignedForm }> => {
	const response = await http.get(`${API_BASE_URL}/patient/forms/render/${assignedFormId}`, {
		params: formTokenParams(token),
	});
	return { form: response.data.form, assigned: response.data.assignment };
};
